import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Eye,
    MousePointerClick,
    CalendarDays,
    Phone,
    MessageCircle,
    Sparkles,
    TrendingUp,
    Lock,
    Wallet,
    Package,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import { getOwnerStats } from "@/lib/analyticsStore";
import { getBookingsBySeller } from "@/lib/bookingStore";
import { getWallet } from "@/lib/monetizationStore";

const Insights = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isPremium = !!user?.premium;
    const isBusiness = user?.accountType === "business";

    const stats = useMemo(() => {
        if (!user) return [];
        const a = getOwnerStats(user.id);
        const bookings = getBookingsBySeller(user.id) || [];
        const released = bookings.filter((b) => b.status === "released");
        const earnings = released.reduce((acc, b) => acc + (b.amount || 0), 0);
        const wallet = getWallet(user.id);
        return [
            { id: "views",   icon: Eye,               label: "Profile views",     value: a.views,  delta: `${a.services + a.events} listings` },
            { id: "clicks",  icon: MousePointerClick, label: "Service clicks",    value: a.clicks, delta: `${a.conversion}% conversion` },
            { id: "bookings",icon: Package,           label: "Bookings",          value: a.bookings, delta: `${released.length} completed` },
            { id: "earnings",icon: Wallet,            label: "Earnings",          value: `₦${earnings.toLocaleString()}`, delta: `Avail. ₦${(wallet.available || 0).toLocaleString()}` },
            { id: "msgs",    icon: MessageCircle,     label: "Messages received", value: 0,         delta: "—" },
            { id: "boost",   icon: TrendingUp,        label: "Boost performance", value: "x1.8",    delta: "CTR lift" },
        ];
    }, [user]);

    return (
        <div className="screen" data-testid="insights-screen">
            <PageHeader title="Insights" subtitle="Your reach at a glance" />

            <div className="flex-1 overflow-y-auto no-scrollbar">
                <div className={["relative grid grid-cols-2 gap-3 p-4", isPremium ? "" : "select-none"].join(" ")}>
                    {stats.map((s) => (
                        <StatCard key={s.id} {...s} locked={!isPremium} />
                    ))}

                    {!isPremium && (
                        <div
                            className="absolute inset-0 flex flex-col items-center justify-center text-center px-8"
                            data-testid="insights-locked-overlay"
                            style={{
                                background:
                                    "linear-gradient(180deg, hsl(var(--background) / 0.25) 0%, hsl(var(--background) / 0.88) 55%, hsl(var(--background)) 100%)",
                                backdropFilter: "blur(6px)",
                                WebkitBackdropFilter: "blur(6px)",
                            }}
                        >
                            <span
                                className="h-14 w-14 rounded-full flex items-center justify-center text-primary-foreground shadow-card"
                                style={{
                                    background:
                                        "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                }}
                            >
                                <Lock size={22} />
                            </span>
                            <h2 className="mt-4 font-display text-[18px] font-semibold">Insights are Premium</h2>
                            <p className="mt-1 text-[13px] text-muted-foreground max-w-xs">
                                See who's viewing you, what's working, and grow smarter.
                            </p>
                            <div className="mt-5 w-full max-w-xs">
                                <GradientButton
                                    testId="insights-upgrade-btn"
                                    onClick={() => navigate("/premium")}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Sparkles size={16} />
                                        Upgrade to Premium to unlock Insights
                                    </span>
                                </GradientButton>
                            </div>
                        </div>
                    )}
                </div>

                {isPremium && <BoostSection />}

                {isPremium && isBusiness && (
                    <section className="px-4 mt-4" data-testid="insights-business-section">
                        <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                            Business summary
                        </h3>
                        <div className="rounded-2xl border border-border bg-card p-4 text-[12.5px] text-muted-foreground">
                            Views convert to bookings at your current rate. Boost a top listing to lift reach, and keep your portfolio fresh to drive clicks.
                        </div>
                    </section>
                )}

                <div className="h-10" />
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, delta, locked }) => (
    <div
        data-testid={`insight-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="relative p-4 rounded-2xl border border-border bg-card shadow-soft"
    >
        <span
            className="h-9 w-9 rounded-xl flex items-center justify-center text-primary"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.06) 60%, transparent 100%)",
            }}
        >
            <Icon size={16} strokeWidth={2.3} />
        </span>
        <div className="mt-3 font-display text-[22px] font-semibold tracking-tight">
            {locked ? "—" : value}
        </div>
        <div className="text-[11.5px] text-muted-foreground mt-0.5">{label}</div>
        <div className="text-[10.5px] text-primary font-medium mt-1.5">
            {locked ? "Locked" : delta}
        </div>
    </div>
);

const BoostSection = () => (
    <section className="px-4 mt-6" data-testid="insights-boost-section">
        <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
            Boost performance
        </h3>
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium">Active boosts</span>
                <span className="text-[12.5px] text-primary font-semibold">+145 extra reach</span>
            </div>
            <p className="text-[11.5px] text-muted-foreground mt-1">
                Stats update within a few seconds of going live.
            </p>
        </div>
    </section>
);

export default Insights;
